<template>
  <app-filters-drawer :model-value="isOpen" :title="$t('projects.ProjectsFiltersPane.filters')"
                      data-locator="projects-filters-pane"
                      @close="closePane" @clear="deleteAllFilters" @apply="applyFilters"
  >
    <template #body>
      <div v-if="isOpen">
        <div class="heading-sm-bold text-text-primary mb-2">
          {{ $t('projects.ProjectsFiltersPane.projectType') }}
        </div>
        <v-row no-gutters class="mb-4">
          <v-col v-for="(projectType, index) in projectTypes"
                 :key="`projectType-${index}`"
                 cols="12"
          >
            <v-checkbox v-model="filters.projectType"
                        :label="$t(`common.projectTypes.${projectType}`)"
                        :value="projectType"
                        hide-details
                        multiple
                        density="compact"
                        class="mb-1"
            />
          </v-col>
        </v-row>

        <div class="heading-sm-bold text-text-primary mb-2">
          {{ $t('projects.ProjectsFiltersPane.role') }}
        </div>
        <v-row no-gutters class="mb-4">
          <v-col v-for="(role, index) in roles"
                 :key="`role-${index}`"
                 cols="12"
          >
            <v-checkbox v-model="filters.role"
                        :label="$t(`projects.ProjectsFiltersPane.roles.${role}`)"
                        :value="role"
                        hide-details
                        multiple
                        density="compact"
                        class="mb-1"
            />
          </v-col>
        </v-row>

        <div class="heading-sm-bold text-text-primary mb-3">
          {{ $t('projects.ProjectsFiltersPane.otherSettings') }}
        </div>
        <ProjectsFiltersMetadataGroupe @update:metadata="onMetadataUpdate"/>

        <template v-if="existingRoomsLabelsPending || existingRoomsLabelsError">
          <div v-if="existingRoomsLabelsPending" class="d-flex justify-center">
            <v-skeleton-loader type="text" />
          </div>
          <p v-else-if="existingRoomsLabelsError" class="text-error text-caption mt-2">
            {{ $t('projects.ProjectsFiltersPane.getAccessibleLabelsError') }}
          </p>
        </template>
        <template v-else>
          <div class="heading-xs-bold text-text-secondary mb-2">
            {{ $t('projects.ProjectsFiltersPane.labels') }}
          </div>
          <AppMultiSelect
            v-model="filters.selectedLabels"
            :items="existingRoomsLabels"
            :item-title="item => item.label[$i18n.locale]"
            :item-value="item => item"
            :placeholder="$t('projects.ProjectsFiltersPane.labels')"
            data-locator="status-filter-select"
          >
            <template #no-data>
              <ProjectsFiltersNoData :label="$t('projects.ProjectsFiltersPane.labels')" />
            </template>
          </AppMultiSelect>
        </template>
        <div class="mb-4">
          <div class="heading-xs-bold text-text-secondary mb-2">
            {{ $t('projects.ProjectsFiltersPane.creationDate') }}
          </div>
          <AppDateRangePicker
            :label="$t('projects.ProjectsFiltersPane.creationDate')"
            :model-value="filters.creationDate"
            @update:model-value="(value) => handleDateRangeChange(value, 'creationDate')"
          />
        </div>
        <div>
          <div class="heading-xs-bold text-text-secondary mb-2">
            {{ $t('projects.ProjectsFiltersPane.dueDate') }}
          </div>
          <AppDateRangePicker
            :label="$t('projects.ProjectsFiltersPane.dueDate')"
            :model-value="filters.dueDate"
            @update:model-value="(value) => handleDateRangeChange(value, 'dueDate')"
          />
        </div>
      </div>
    </template>
  </app-filters-drawer>
</template>

<script>
import { mapState, mapActions } from 'vuex'

import AppDateRangePicker from '@/common/AppDateRangePicker.vue'
import AppMultiSelect from '@/common/AppMultiSelect.vue'
import { GET_EXISTING_ROOMS_LABELS } from '@/store/modules/rooms/action_types'
import { initialRoomFiltersPaneState } from '@/store/modules/rooms/state.js'

import ProjectsFiltersMetadataGroupe from './ProjectsFiltersMetadataGroupe.vue'
import ProjectsFiltersNoData from './ProjectsFiltersNoData.vue'

export default {
  name: 'ProjectsFiltersPane',
  components: {
    AppDateRangePicker,
    AppMultiSelect,
    ProjectsFiltersMetadataGroupe,
    ProjectsFiltersNoData,
  },
  data () {
    return {
      filters: null,
      projectTypes: ['project', 'dataroom'],
      roles: ['projectManager', 'guest'],
    }
  },
  computed: {
    ...mapState('rooms', [
      'filtersPane',
      'existingRoomsLabelsPending',
      'existingRoomsLabels',
      'existingRoomsLabelsError',
    ]),
    isOpen () {
      return this.filtersPane.isOpen
    },
  },
  watch: {
    isOpen (value) {
      if (value) {
        this.filters = { ...this.filtersPane.filters }
        this.GET_EXISTING_ROOMS_LABELS()
      }
    },
  },
  methods: {
    ...mapActions('rooms', [GET_EXISTING_ROOMS_LABELS]),
    closePane () {
      this.$store.commit('rooms/CLOSE_FILTER_PANE')
    },
    handleDateRangeChange (value, property) {
      this.filters[property] = value
    },
    deleteAllFilters () {
      this.filters = { ...initialRoomFiltersPaneState }
      this.$store.commit('rooms/DELETE_FILTERS')
    },
    applyFilters () {
      this.$store.commit('rooms/SET_FILTERS', this.filters)
      this.closePane()
    },
    onMetadataUpdate(selectedMetadata) {
      if (!this.filters.metadata) {
        this.filters.metadata = {}
      }
      this.filters.metadata = { ...selectedMetadata }
    },
  },
}
</script>
<style scoped>
:deep(.v-selection-control__wrapper) {
  justify-content: flex-start;
}
</style>
